import { PDF_CONSTANTS } from '../constants/reportConstants';
import { CompressionError, TimeoutError, withTimeout } from './errorHandling';

/**
 * Loads an image from URL with timeout
 */
export function loadImage(url: string, timeoutMs: number = PDF_CONSTANTS.IMAGE_LOAD_TIMEOUT): Promise<HTMLImageElement> {
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));

    img.src = url;
  });

  return withTimeout(promise, timeoutMs, `Image load timeout after ${timeoutMs}ms`);
}

/**
 * Creates a canvas element with specified dimensions
 */
export function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/**
 * Converts URL to base64 with timeout
 */
export async function urlToBase64(
  url: string,
  timeoutMs: number = PDF_CONSTANTS.IMAGE_LOAD_TIMEOUT
): Promise<string> {
  try {
    const img = await loadImage(url, timeoutMs);

    const canvas = createCanvas(img.width, img.height);
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw error;
    }
    throw new Error(`Failed to convert URL to base64: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Compresses an image to reduce file size
 */
export async function compressImage(
  base64Image: string,
  quality: number = PDF_CONSTANTS.COMPRESSION_QUALITY,
  maxWidth: number = PDF_CONSTANTS.COMPRESSION_MAX_WIDTH,
  maxHeight: number = PDF_CONSTANTS.COMPRESSION_MAX_HEIGHT
): Promise<string> {
  try {
    // Load the image
    const img = await loadImage(base64Image);

    // Calculate new dimensions while maintaining aspect ratio
    let width = img.width;
    let height = img.height;

    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }

    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    // Create canvas and draw resized image
    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new CompressionError('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0, width, height);

    // Convert to compressed base64
    const compressed = canvas.toDataURL('image/jpeg', quality);

    // Check if compression actually reduced size
    if (compressed.length >= base64Image.length) {
      // Use original if compression didn't help
      return base64Image;
    }

    return compressed;
  } catch (error) {
    if (error instanceof CompressionError) {
      throw error;
    }
    throw new CompressionError(
      `Failed to compress image: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Calculates image dimensions to fit within max bounds while maintaining aspect ratio
 */
export function calculateImageDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Scale down if larger than max width
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  // Scale down if larger than max height
  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return { width, height };
}

/**
 * Gets the size of a base64 string in bytes
 */
export function getBase64Size(base64String: string): number {
  // Remove data URL prefix if present
  const base64Data = base64String.replace(/^data:image\/\w+;base64,/, '');

  // Calculate size (base64 is ~4/3 the size of original)
  const padding = (base64Data.match(/=/g) || []).length;
  return (base64Data.length * 3) / 4 - padding;
}

/**
 * Checks if total payload size exceeds limit
 */
export function validatePayloadSize(
  images: string[],
  maxSize: number = PDF_CONSTANTS.MAX_PAYLOAD_SIZE
): { isValid: boolean; totalSize: number; maxSize: number } {
  const totalSize = images.reduce((sum, img) => sum + getBase64Size(img), 0);

  return {
    isValid: totalSize <= maxSize,
    totalSize,
    maxSize,
  };
}

/**
 * Extracts dimensions from base64 image
 */
export async function getImageDimensions(base64Image: string): Promise<{ width: number; height: number }> {
  const img = await loadImage(base64Image);
  return { width: img.width, height: img.height };
}
